/**
 * The three picker sources behind `LinkControl` — "where does this button go?".
 *
 * ## Why these are not the catalog pickers we already have
 *
 * `productPickerSource` / `collectionPickerSource` are keyed by **id**, because a `product-picker` field
 * stores `{kind:'product', id}` and the renderer resolves the entity from the page bundle. A LINK does not:
 * `resolveUrl` builds `/products/<slug>`, `/collections/<slug>`, `/pages/<handle>` — it reads the **slug**
 * and nothing else. Feeding an id into a link would produce a 404 that only shows up on the live store.
 *
 * So these sources present the same rows with the SLUG as the picker's key. Same endpoints, same ACTIVE-only
 * filtering (a DRAFT target resolves to nothing on the storefront), same shared client cache.
 *
 * `fetchOne` never hits the network: the endpoints look entities up by id, not by slug, and there is nothing
 * useful to ask them. When a stored slug is not on the first page of results the trigger simply shows the
 * slug itself — the honest answer, and still the value the storefront will use.
 *
 * Replaces the M4 stub — a bare "product handle (e.g. my-product)" text box the seller had to type from
 * memory, with no way to know whether what they typed exists (founder, 2026-08-27).
 */
import { apiRequest } from './http';
import { builderApi } from './builderApi';
import type { PickerItem, PickerSource } from '../inspector/controls/EntityPickerListbox';
import { createCachedPickerSource } from './pickerCache';

const PICKER_LIMIT = '50';

interface SlugRow {
  slug: string;
  name: string;
  status: string;
  coverImageUrl: string | null;
}
interface SlugListResponse {
  items: SlugRow[];
}

/** A slug the picker could not name — the trigger shows the slug, which is what the store will use. */
function slugAsItem(slug: string): PickerItem {
  return { id: slug, name: slug };
}

/** GET a catalog list and key it by SLUG, keeping only what the storefront can actually resolve. */
async function searchBySlug(path: string, term: string): Promise<PickerItem[]> {
  const query: Record<string, string> = { limit: PICKER_LIMIT };
  if (term) query.search = term;
  const res = await apiRequest<SlugListResponse>(path, { query });
  return res.items
    .filter((row) => row.status === 'ACTIVE' && row.slug)
    .map((row) => ({ id: row.slug, name: row.name, thumbnailUrl: row.coverImageUrl }));
}

/** Products, keyed by slug — for a link whose target is a product page. */
export const productLinkPickerSource: PickerSource = createCachedPickerSource({
  search: (term) => searchBySlug('/api/products', term),
  fetchOne: async (slug) => slugAsItem(slug),
});

/** Collections, keyed by slug — for a link whose target is a collection page. */
export const collectionLinkPickerSource: PickerSource = createCachedPickerSource({
  search: (term) => searchBySlug('/api/collections', term),
  fetchOne: async (slug) => slugAsItem(slug),
});

/**
 * The store's own CMS pages, keyed by handle. `listPages` returns the whole list in one call (a store has
 * a handful of pages, not a catalog), so the search term filters it client-side — the one place where that
 * is right rather than a way to hide older entries.
 */
export const pageLinkPickerSource: PickerSource = createCachedPickerSource({
  search: async (term) => {
    const { pages } = await builderApi.listPages();
    const needle = term.trim().toLowerCase();
    return pages
      .filter((p) => p.status === 'PUBLISHED')
      .map((p) => ({ id: p.handle, name: p.title ?? p.handle }))
      .filter((it) => !needle || it.name.toLowerCase().includes(needle) || it.id.includes(needle));
  },
  fetchOne: async (handle) => slugAsItem(handle),
});
